// Storm Tracker Types
// Types for the Storm Tracker feature - severe weather monitoring and alerts

export type EventType = 'hail' | 'wind' | 'tornado';
export type SeverityLevel = 'minor' | 'moderate' | 'severe' | 'extreme';

export interface StormTerritory {
  id: number;
  name: string;
  // Geographic scope - either ZIP codes or radius
  zip_codes?: string[];
  counties?: string[];
  center_latitude?: number;
  center_longitude?: number;
  radius_miles?: number;
  // Alert thresholds
  min_hail_size_inches: number;
  min_wind_speed_mph: number;
  include_tornadoes: boolean;
  // Settings
  notify_via_sms: boolean;
  notify_via_push: boolean;
  is_active: boolean;
  quiet_hours_start?: string | null;  // e.g., "22:00"
  quiet_hours_end?: string | null;    // e.g., "07:00"
  // Metadata
  organization: string;
  created_at: string;
  updated_at: string;
  // Computed fields from API
  subscriber_count?: number;
  alert_count_7d?: number;
  is_subscribed?: boolean;
  // Auto-generated per-rep watchlist (set by RepWatchlistService).
  is_auto_generated?: boolean;
  assigned_user?: number | null;
}

export interface StormTerritoryFormData {
  name: string;
  zip_codes?: string[];
  counties?: string[];
  center_latitude?: number;
  center_longitude?: number;
  radius_miles?: number;
  min_hail_size_inches: number;
  min_wind_speed_mph: number;
  include_tornadoes: boolean;
  notify_via_sms: boolean;
  notify_via_push: boolean;
  is_active: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
}

export type PriorityTier = 'low' | 'medium' | 'high';

export interface StormAlert {
  id: number;
  territory: number;
  territory_name?: string;
  // Event details
  event_type: EventType;
  severity: SeverityLevel;
  opportunity_score: number; // 0-100
  priority_tier: PriorityTier;
  // Location
  latitude: number;
  longitude: number;
  city?: string;
  county?: string;
  state?: string;
  zip_code?: string;
  // Event magnitude
  magnitude?: number | null;
  magnitude_display: string;
  hail_size_inches?: number;
  wind_speed_mph?: number;
  tornado_rating?: string; // EF0, EF1, etc.
  // Source info
  data_source: string;
  nws_event_id?: string;
  external_id?: string;
  raw_data?: Record<string, unknown>;
  // Canonical timestamp emitted by the Storm Alert API.
  event_timestamp: string;
  hours_ago: number;
  // Sharing
  share_url?: string;
  created_at: string;
  updated_at: string;
}

export interface StormSubscription {
  id: number;
  user: number;
  user_name?: string;
  user_email?: string;
  territory: number;
  territory_name?: string;
  // Notification preferences
  notify_via_sms: boolean;
  notify_via_push: boolean;
  min_severity: SeverityLevel;
  min_opportunity_score: number;
  // Status
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StormSubscriptionFormData {
  territory: number;
  notify_via_sms: boolean;
  notify_via_push: boolean;
  min_severity: SeverityLevel;
  min_opportunity_score: number;
  is_active: boolean;
}

export interface StormNotification {
  id: number;
  alert: number;
  subscription: number;
  user: number;
  // Delivery details
  notification_type: 'sms' | 'push' | 'email';
  message: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  // Status
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  error_message?: string;
  created_at: string;
}

export interface StormStats {
  alerts_24h: number;
  alerts_7d: number;
  high_priority_alerts: number;
  active_territories: number;
  user_subscriptions: number;
  // Breakdown by type
  alerts_by_type: {
    hail: number;
    wind: number;
    tornado: number;
  };
  // Breakdown by severity
  alerts_by_severity: {
    minor: number;
    moderate: number;
    severe: number;
    extreme: number;
  };
}

// API response types
export interface StormAlertsListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: StormAlert[];
}

export interface StormTerritoriesListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: StormTerritory[];
}

// Filter types
export interface StormAlertFilters {
  territory?: number;
  event_type?: EventType;
  severity?: SeverityLevel;
  min_score?: number;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

// UI helper types
export interface OpportunityScoreTier {
  label: string;
  color: 'warning' | 'primary' | 'neutral';
  min: number;
  max: number;
}

export const OPPORTUNITY_SCORE_TIERS: OpportunityScoreTier[] = [
  { label: 'High', color: 'warning', min: 71, max: 100 },
  { label: 'Medium', color: 'primary', min: 31, max: 70 },
  { label: 'Low', color: 'neutral', min: 0, max: 30 },
];

export const SEVERITY_COLORS: Record<SeverityLevel, 'neutral' | 'warning' | 'danger'> = {
  minor: 'neutral',
  moderate: 'warning',
  severe: 'danger',
  extreme: 'danger',
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  hail: 'Hail',
  wind: 'Wind',
  tornado: 'Tornado',
};

export const SEVERITY_LABELS: Record<SeverityLevel, string> = {
  minor: 'Minor',
  moderate: 'Moderate',
  severe: 'Severe',
  extreme: 'Extreme',
};

// Helper function to get opportunity score tier
export const getOpportunityScoreTier = (score: number): OpportunityScoreTier => {
  return OPPORTUNITY_SCORE_TIERS.find(tier => score >= tier.min && score <= tier.max)
    ?? OPPORTUNITY_SCORE_TIERS[2]; // Default to low
};
